import React from "react";
import PropTypes from "prop-types";
import { Button as MuiButton, CircularProgress, makeStyles } from "@material-ui/core";
import clsx from "clsx";

const useStyles = makeStyles(() => {
  const primaryContained = {
    backgroundColor: "var(--secondary-6)",
    color: "#FFFFFF",
    border: "1px solid transparent",
    "&:hover": {
      backgroundColor: "var(--secondary-4)",
      borderColor: "transparent",
    },
    "&.Mui-disabled": {
      backgroundColor: "var(--secondary-6)",
      color: "#FFFFFF",
      opacity: 0.5,
      borderColor: "transparent",
    },
  };

  const defaultContained = {
    backgroundColor: "#FFFFFF",
    color: "var(--gray-9)",
    border: "1px solid var(--main-10)",
    "&:hover": {
      backgroundColor: "var(--gray-2)",
      borderColor: "var(--main-10)",
    },
    "&.Mui-disabled": {
      backgroundColor: "var(--gray-4)",
      color: "var(--gray-6)",
      borderColor: "var(--gray-4)",
    },
  };

  const primaryOutlined = {
    backgroundColor: "transparent",
    color: "var(--secondary-6)",
    border: "1px solid var(--secondary-6)",
    "&:hover": {
      backgroundColor: "var(--secondary-1)",
      borderColor: "var(--secondary-6)",
    },
    "&.Mui-disabled": {
      color: "var(--gray-5)",
      borderColor: "var(--gray-4)",
    },
  };

  const defaultOutlined = {
    backgroundColor: "transparent",
    color: "var(--gray-9)",
    border: "1px solid var(--main-10)",
    "&:hover": {
      backgroundColor: "var(--gray-2)",
      borderColor: "var(--main-10)",
    },
    "&.Mui-disabled": {
      color: "var(--gray-5)",
      borderColor: "var(--gray-4)",
    },
  };

  const dangerContained = {
    backgroundColor: "var(--auxiliary-red-5)",
    color: "#FFFFFF",
    border: "1px solid transparent",
    "&:hover": {
      backgroundColor: "var(--auxiliary-red-4)",
      borderColor: "transparent",
    },
    "&.Mui-disabled": {
      backgroundColor: "var(--gray-4)",
      color: "var(--gray-6)",
      opacity: 0.7,
      borderColor: "transparent",
    },
  };

  const dangerOutlined = {
    backgroundColor: "transparent",
    color: "var(--auxiliary-red-5)",
    border: "1px solid var(--auxiliary-red-5)",
    "&:hover": {
      backgroundColor: "var(--auxiliary-red-1)",
      borderColor: "var(--auxiliary-red-5)",
    },
    "&.Mui-disabled": {
      color: "var(--gray-5)",
      borderColor: "var(--gray-4)",
    },
  };

  return {
    root: {
      boxSizing: "border-box",
      textTransform: "none",
      fontFamily: '"Nunito Sans", sans-serif',
      fontWeight: 600,
      fontSize: 16,
      lineHeight: "24px",
      textAlign: "center",
      borderRadius: 12,
      boxShadow: "none",
      padding: "16px 20px",
      gap: 8,
    },
    primaryContained,
    primaryOutlined,
    defaultContained,
    defaultOutlined,
    dangerContained,
    dangerOutlined,
    heightSm: {
      padding: "10px 16px",
      minHeight: 40,
    },
    heightMd: {
      padding: "16px 20px",
      minHeight: 56,
    },
    heightLg: {
      padding: "20px 24px",
      minHeight: 56,
    },
    startIcon: { marginRight: 8 },
    endIcon: { marginLeft: 8 },
  };
});

const sizeToMui = { sm: "small", md: "medium", lg: "large" };

function Button({
  children,
  variant = "primary",
  size = "md",
  height,
  outlined: isOutlined = false,
  fullWidth = false,
  disabled = false,
  loading = false,
  startIcon,
  endIcon,
  className,
  ...rest
}) {
  const classes = useStyles();
  const heightKey = height || size;

  const variantClassMap = {
    primary: { contained: classes.primaryContained, outlined: classes.primaryOutlined },
    default: { contained: classes.defaultContained, outlined: classes.defaultOutlined },
    danger: { contained: classes.dangerContained, outlined: classes.dangerOutlined },
  };

  const variantClass = variantClassMap[variant]?.[isOutlined ? "outlined" : "contained"] ?? variantClassMap.primary.contained;

  const heightClassMap = { sm: classes.heightSm, md: classes.heightMd, lg: classes.heightLg };
  const heightClass = heightClassMap[heightKey] ?? classes.heightMd;

  return (
    <MuiButton
      variant={isOutlined ? "outlined" : "contained"}
      size={sizeToMui[size] || "medium"}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      startIcon={loading ? null : startIcon}
      endIcon={loading ? null : endIcon}
      classes={{
        root: clsx(classes.root, variantClass, heightClass, className),
        startIcon: classes.startIcon,
        endIcon: classes.endIcon,
      }}
      {...rest}
    >
      {loading ? <CircularProgress size={20} color="inherit" /> : children}
    </MuiButton>
  );
}

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(["primary", "default", "danger"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  height: PropTypes.oneOf(["sm", "md", "lg"]),
  outlined: PropTypes.bool,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  className: PropTypes.string,
};

export default Button;
